import React, { useCallback, useState } from 'react'
import { Box, Button, Divider, Flex, Link, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from 'constants/interfaces'
import useAvatar from 'hooks/useAvatar'
import { logoutHandler } from 'redux/actions/auth'
import { setLocale } from 'redux/actions/general'
import LocaleText from 'components/LocaleText'
import { ReactComponent as IconMenuBack } from 'assets/icon-usermenu-back.svg'
import { ReactComponent as IconLocale } from 'assets/icon-usermenu-language.svg'
import { ReactComponent as IconLogout } from 'assets/icon-usermenu-logout.svg'
import { ReactComponent as IconMore } from 'assets/icon-usermenu-more.svg'
import { ReactComponent as IconReports } from 'assets/icon-usermenu-repots.svg'
import { BsPuzzle } from 'react-icons/bs'
import { IconWrapper, MiniDrawer } from 'components/helperStyles'
import { privacyPolicy, termsOfUse } from 'constants/endpoints'
import { WhatsAppButton } from 'components/Header/WhatsAppButton'
import { whatsAppUrlSelector } from 'redux/selectors/config'
import { UserMenuItem } from 'components/CommonComponents/UserMenu/UserMenuItem'
import { UserDetails } from 'components/Header/UserDetails'
import UploadAvatarPopUp from 'components/UploadAvatarPopUp'
import { clearAllSetupData, isSetupAllowedForUser } from 'utils/setupStorage'
import { MoreHorizontal, RotateCcw } from 'lucide-react'
import Avatar from 'components/CommonComponents/Avatar'
import keys from 'constants/keys'
import colors from 'constants/colors'
import { isRtlSelector } from 'redux/selectors/general'
import { getLanguageName } from 'utils/languageName'

const DEPLOY_SIGNATURE = `${process.env.REACT_APP_DEPLOY_SIGNATURE}`

const SidebarUserCard: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { myUID, first_name, last_name, user_role } = useSelector((state: RootState) => state.config.config)
  const { retailConfig, translationsKeys, translations } = useSelector((state: RootState) => state.config)
  const locale = useSelector((state: RootState) => state.general.locale)
  const whatsAppUrl = useSelector(whatsAppUrlSelector)
  const isRtl = useSelector(isRtlSelector)
  const avatar = useAvatar(myUID)
  const [showLocaleDrawer, setLocaleDrawer] = useState(false)
  const [showSettingsDrawer, setSettingsDrawer] = useState(false)
  const [isUploadAvatarOpen, setIsUploadAvatarOpen] = useState(false)

  const isSetupAllowed = isSetupAllowedForUser(myUID)
  const fullName = `${first_name || ''} ${last_name || ''}`.trim()
  const roleKey = String(user_role) as unknown as keyof typeof keys.USER_ROLES
  const roleLocaleKey = keys.USER_ROLES[roleKey]

  const openUploadAvatar = useCallback(() => setIsUploadAvatarOpen(true), [])
  const closeUploadAvatar = useCallback(() => setIsUploadAvatarOpen(false), [])

  const handleLogout = () => dispatch(logoutHandler())
  const handleLocale = (item: string) => dispatch(setLocale(item))

  const handleResetSetup = () => {
    if (window.confirm('Are you sure you want to reset ALL setup data? This will clear setup for all users.')) {
      clearAllSetupData()
      window.location.reload()
    }
  }

  return (
    <>
      <Menu closeOnBlur>
        <MenuButton
          {...({
            as: Button,
            width: '100%',
            height: 'auto',
            p: '8px',
            variant: 'ghost',
            borderRadius: '10px',
            _hover: { bg: '#f1f1f1' },
            _focus: { bg: 'transparent' },
            _active: { bg: 'transparent' },
          } as any)}
        >
          <Flex align="center" width="100%">
            <Box position="relative" flexShrink={0}>
              <Avatar src={avatar} width="38px" height="38px" />
              <Box
                position="absolute"
                bottom="1px"
                right="1px"
                width="10px"
                height="10px"
                borderRadius="50%"
                bg="#22c55e"
                style={{ border: '2px solid white' }}
              />
            </Box>
            <Box mx="10px" flex="1" overflow="hidden" textAlign={isRtl ? 'right' : 'left'}>
              <Text
                fontSize="14px"
                fontWeight="600"
                color="#1a202c"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {fullName}
              </Text>
              {roleLocaleKey && (
                <Text fontSize="12px" color="#6b6b6b">
                  <LocaleText text={roleLocaleKey} />
                </Text>
              )}
            </Box>
            <MoreHorizontal size={16} color="#6b6b6b" style={{ flexShrink: 0 }} />
          </Flex>
        </MenuButton>

        <MenuList
          style={{
            width: '300px',
            overflow: 'hidden',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 14px 0 rgba(0,0,0,0.05)',
          }}
          placement="top-end"
        >
          {showLocaleDrawer && (
            <MiniDrawer
              className="locale-drawer locale-user-dropdown"
              isShow={showLocaleDrawer}
              style={{ zIndex: 9999 }}
            >
              <Flex alignItems="center" cursor="pointer" pb="20px" px="20px" onClick={() => setLocaleDrawer(false)}>
                <IconMenuBack style={{ transform: isRtl ? 'scaleX(-1)' : undefined }} />
                <Box fontSize="20px" fontWeight="900" mx="20px">
                  <LocaleText text="language" />
                </Box>
              </Flex>
              {translationsKeys.map((item: string, index: number) => (
                <MenuItem
                  key={index}
                  onClick={() => handleLocale(item)}
                  textTransform="capitalize"
                  cursor="pointer"
                  style={{ borderRadius: '4px', fontWeight: locale === item ? 'bold' : 'inherit' }}
                >
                  {getLanguageName(item, translations?.[item])}
                </MenuItem>
              ))}
            </MiniDrawer>
          )}

          {showSettingsDrawer && (
            <MiniDrawer className="locale-drawer" isShow={showSettingsDrawer}>
              <Flex alignItems="center" cursor="pointer" pb="20px" px="20px" onClick={() => setSettingsDrawer(false)}>
                <IconMenuBack style={{ transform: isRtl ? 'scaleX(-1)' : undefined }} />
              </Flex>
              <MenuItem onClick={() => navigate('/custom-reports')} textTransform="capitalize" cursor="pointer">
                <IconWrapper>
                  <IconReports />
                </IconWrapper>
                <Text mx="20px">Custom Reports</Text>
              </MenuItem>
            </MiniDrawer>
          )}

          <MenuItem
            p={0}
            onMouseDown={() => {
              requestAnimationFrame(() => openUploadAvatar())
            }}
            _hover={{ bg: 'transparent' }}
            _focus={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
          >
            <UserDetails />
          </MenuItem>

          <Divider borderColor="#e2e8f0" mx="12px" />

          {whatsAppUrl && <WhatsAppButton px="1rem" href={whatsAppUrl} />}
          <UserMenuItem px="1rem" literal="language" onClick={() => setLocaleDrawer(true)} icon={<IconLocale />}>
            <IconMore style={{ transform: locale === 'he' ? 'rotate(180deg)' : 'none' }} />
          </UserMenuItem>
          <UserMenuItem
            px="1rem"
            icon={<IconLogout />}
            literal="logout"
            onClick={handleLogout}
            textColor={colors.red}
          />
          {!!retailConfig?.integrations?.length && (
            <UserMenuItem
              px="1rem"
              icon={<BsPuzzle />}
              literal="integrations"
              onClick={() => navigate(keys.ROUTE_NAMES.INTEGRATIONS)}
            />
          )}
          {isSetupAllowed && (
            <UserMenuItem px="1rem" literal="reset_setup" onClick={handleResetSetup} icon={<RotateCcw size={16} />} />
          )}
          <Box px="1rem" pt="20px" pb="10px" fontSize="12px" color={colors.greyDark}>
            <Link href={privacyPolicy}>
              <LocaleText text="legal_privacy" />
            </Link>
            &nbsp;·&nbsp;
            <Link href={termsOfUse}>
              <LocaleText text="legal_terms" />
            </Link>
            &nbsp;·&nbsp;
            <br />
            {DEPLOY_SIGNATURE}
          </Box>
        </MenuList>
      </Menu>

      <UploadAvatarPopUp
        isOpen={isUploadAvatarOpen}
        onClose={closeUploadAvatar}
        onUploadFromGallery={closeUploadAvatar}
        onTakeSelfie={closeUploadAvatar}
        onRemindLater={closeUploadAvatar}
      />
    </>
  )
}

export default SidebarUserCard
